import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  ChevronLeft,
  Plus,
  Pencil,
  Trash2,
  Star,
  Loader2,
  Home,
  Briefcase,
  Building
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';

import { API } from '@/config/api';

const LABEL_ICONS = {
  Home: Home,
  Work: Briefcase,
  Other: Building
};

export default function AddressBookPage() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    label: 'Home',
    full_address: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    delivery_instructions: '',
    is_default: false
  });

  const fetchAddresses = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data);
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const openDialog = (address = null) => {
    if (address) {
      setEditing(address.id);
      setForm({
        label: address.label,
        full_address: address.full_address,
        street: address.street || '',
        city: address.city || '',
        state: address.state || '',
        zip_code: address.zip_code || '',
        delivery_instructions: address.delivery_instructions || '',
        is_default: address.is_default
      });
    } else {
      setEditing(null);
      setForm({
        label: 'Home',
        full_address: '',
        street: '',
        city: '',
        state: '',
        zip_code: '',
        delivery_instructions: '',
        is_default: addresses.length === 0
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.full_address.trim()) {
      toast.error('Please enter a full address');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        const response = await axios.put(`${API}/addresses/${editing}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAddresses(addresses.map(a => a.id === editing ? response.data : a));
        toast.success('Address updated');
      } else {
        const response = await axios.post(`${API}/addresses`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAddresses([response.data, ...addresses]);
        toast.success('Address added');
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const deleteAddress = async (id) => {
    try {
      await axios.delete(`${API}/addresses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(addresses.filter(a => a.id !== id));
      toast.success('Address deleted');
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const setDefault = async (id) => {
    try {
      await axios.post(`${API}/addresses/${id}/set-default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(addresses.map(a => ({ ...a, is_default: a.id === id })));
      toast.success('Default address updated');
    } catch (error) {
      toast.error('Failed to update default address');
    }
  };

  const LabelIcon = (label) => LABEL_ICONS[label] || MapPin;

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="address-book-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">Address Book</h1>
          </div>
          <Button onClick={() => openDialog()} className="btn-pill">
            <Plus className="h-4 w-4 mr-2" />
            Add Address
          </Button>
        </div>

        {/* Addresses List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-5 w-1/4 mb-2" />
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : addresses.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No saved addresses</h3>
            <p className="text-muted-foreground mb-4">
              Add delivery addresses for faster checkout
            </p>
            <Button onClick={() => openDialog()} className="btn-pill">
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Address
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {addresses.map((address) => {
              const Icon = LabelIcon(address.label);
              return (
                <Card key={address.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{address.label}</h3>
                            {address.is_default && (
                              <Badge variant="secondary" className="text-xs">
                                <Star className="h-3 w-3 mr-1" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {address.full_address}
                          </p>
                          {address.delivery_instructions && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              "{address.delivery_instructions}"
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {!address.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDefault(address.id)}
                            className="text-xs"
                          >
                            Set Default
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDialog(address)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => deleteAddress(address.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editing ? 'Edit Address' : 'Add New Address'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Label</Label>
                <Select
                  value={form.label}
                  onValueChange={(v) => setForm({ ...form, label: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Home">Home</SelectItem>
                    <SelectItem value="Work">Work</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Full Address *</Label>
                <Textarea
                  value={form.full_address}
                  onChange={(e) => setForm({ ...form, full_address: e.target.value })}
                  placeholder="123 Main St, Apt 4B, New York, NY 10001"
                  className="resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="New York"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    placeholder="NY"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>ZIP Code</Label>
                <Input
                  value={form.zip_code}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                  placeholder="10001"
                />
              </div>

              <div className="space-y-2">
                <Label>Delivery Instructions</Label>
                <Textarea
                  value={form.delivery_instructions}
                  onChange={(e) => setForm({ ...form, delivery_instructions: e.target.value })}
                  placeholder="Ring doorbell, leave at door, etc."
                  className="resize-none"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {editing ? 'Update' : 'Add'} Address
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
